import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';

import { IconChevronRight, IconX } from './icons';

export type EmptyQueueStateProps = {
	onTryAgain: () => void;
	onBackToQueue: () => void;
	loading?: boolean;
};

/**
 * Empty-queue panel. Renders inline inside the LEFT column when the lead
 * queue is exhausted, instead of taking over the entire viewport — the rest
 * of the focused view (TopBar, center coach idle strip, app drawer rail,
 * right pinned rail) stays interactive so the user can still field incoming
 * calls and access settings.
 */
export const EmptyQueueState: FC<EmptyQueueStateProps> = ({ onTryAgain, onBackToQueue, loading }) => {
	return (
		<div className={css(styles.card)}>
			<div className={css(styles.eyebrow)}>Focused View</div>
			<h2 className={css(styles.title)}>No leads in your queue</h2>
			<p className={css(styles.body)}>
				You've worked through the queue for now. Check back as new leads are added, or head back to the queue to adjust
				your filters.
			</p>
			<div className={css(styles.actions)}>
				<button type='button' onClick={onTryAgain} disabled={loading} className={css(styles.btn, styles.btnPrimary)}>
					{loading ? 'Checking…' : 'Try again'}
					{!loading && <IconChevronRight size={13} sw={2.2} />}
				</button>
				<button type='button' onClick={onBackToQueue} className={css(styles.btn)}>
					<IconX size={13} />
					Back to queue
				</button>
			</div>
		</div>
	);
};

const styles = StyleSheet.create({
	card: {
		margin: 16,
		padding: '20px 16px',
		borderRadius: 10,
		border: '1px dashed var(--border)',
		background: 'var(--bg-elev)',
		textAlign: 'center',
	},
	eyebrow: {
		fontSize: 10,
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: 'var(--ink-faint)',
	},
	title: {
		margin: '6px 0 8px',
		fontSize: 16,
		fontWeight: 600,
		color: 'var(--ink)',
	},
	body: {
		margin: '0 0 14px',
		fontSize: 12.5,
		lineHeight: 1.5,
		color: 'var(--ink-muted)',
	},
	actions: {
		display: 'flex',
		justifyContent: 'center',
		flexWrap: 'wrap',
		gap: 6,
	},
	btn: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		height: 28,
		padding: '0 10px',
		borderRadius: 6,
		fontSize: 12,
		fontWeight: 500,
		fontFamily: 'inherit',
		background: 'var(--bg)',
		color: 'var(--ink)',
		border: '1px solid var(--border)',
		cursor: 'pointer',
		transition: 'background 120ms',
		':hover': { background: 'var(--hover)' },
		':disabled': { opacity: 0.5, cursor: 'not-allowed' },
	},
	btnPrimary: {
		background: 'var(--ink)',
		color: 'var(--bg)',
		borderColor: 'var(--ink)',
		':hover': { opacity: 0.92 },
	},
});
